import { sprite } from '../kit';
import { C, type Pix } from '../pix';
import { registerIcons } from '../store';
import { daoBlade, formedBlade, R, ramp, type Ramp } from './craft';

/**
 * Hand-made item pictures: art manuals (ตำราวิชา). A thread-bound book seen from the front, a
 * little tilted: cover cloth, stitched spine on the right, a paper title slip with ink marks (not
 * real script), and the art's emblem on the cover.
 */

/** The book: cover in `cloth`, pages showing along the bottom and left. */
function book(p: Pix, cloth: Ramp, slip: Ramp = R.paper): void {
  // Pages under the cover.
  p.poly(
    [
      [6, 7],
      [24, 5],
      [26, 28],
      [8, 30],
    ],
    R.paper.d1,
  );
  for (let y = 9; y < 29; y += 3) p.px(6 + Math.round((y - 7) * 0.09), y, R.paper.b);
  // Cover.
  p.poly(
    [
      [8, 5],
      [26, 3],
      [28, 26],
      [10, 28],
    ],
    cloth.b,
  );
  // Shade the lower-left half of the cover in a shaped cluster (cloth sags toward the pages).
  p.poly(
    [
      [9, 17],
      [16, 27],
      [10, 28],
    ],
    cloth.d1,
  );
  p.line(9, 5, 25, 3, cloth.l1);
  // Stitched spine on the right: thread across the edge at four holes.
  p.line(25, 4, 27, 26, cloth.d2);
  for (const y of [6, 11, 16, 21, 25]) {
    p.px(24 + (y - 4) * 0.09, y, C.paper);
    p.line(25 + (y - 4) * 0.09, y, 28 + (y - 4) * 0.09, y, slip.l1);
  }
  // Title slip, top left of the cover, with marks standing in for the title.
  p.poly(
    [
      [11, 6],
      [16, 6],
      [17, 20],
      [12, 20],
    ],
    slip.b,
  );
  p.line(16, 6, 17, 19, slip.d1);
  for (const y of [8, 11, 14, 17]) p.line(13 + (y - 6) * 0.07, y, 15 + (y - 6) * 0.07, y + (y % 2), R.ink.b);
}

const indigo = ramp('#34508e');
const teal = ramp('#2e8a86');
const ochre = ramp('#c8902e');
const navy = ramp('#2a3a6e');

registerIcons('item', {
  // ตำราเพลงกระบี่ร่องศิลาฉบับคัดลอก: indigo cover, a jian across a stone groove.
  'manual-nine-swords': (p) => {
    book(p, indigo);
    p.line(18, 25, 25, 23, indigo.d2);
    p.line(18, 24, 25, 22, indigo.l1);
    formedBlade(p, 19, 21, 25, 6, R.steel, { w: 2, ridge: false });
    p.thick(18, 22, 20, 23, R.gold.b, 1);
    p.px(19, 24, R.red.b);
  },
  // ตำราฝ่ามือเมฆาคล้อยฉบับคัดลอก: teal cover, an open palm rising from a cloud.
  'manual-cloud-palm': (p) => {
    book(p, teal);
    sprite(
      p,
      18,
      8,
      ['.s.s..', '.s.s.s', 'ssssss', 'ssssss', '.ssss.', '.sss..', 'wwwwww', 'wWwwWw', '.wwww.'],
      { s: '#f2c49a', w: R.paper.l1, W: R.paper.l2 },
    );
    p.px(18, 10, '#f2c49a').px(17, 11, '#f2c49a');
    p.line(19, 20, 23, 22, teal.l2);
  },
  // ตำราฝ่ามือแขนยาวทงปี้ (legend): ochre cover, a long reaching arm and a gold seal.
  'manual-wb-long-arm': (p) => {
    book(p, ochre, ramp('#f0e0b0'));
    p.path(
      [
        [19, 25],
        [20, 18],
        [23, 12],
      ],
      ramp('#a86a3a').b,
      2,
    );
    sprite(p, 21, 7, ['.f.f', 'ffff', 'fff.', '.ff.'], { f: '#d8a070' });
    p.rect(19, 22, 4, 3, C.seal);
    p.px(20, 23, R.gold.l2);
    for (const [x, y] of [
      [13, 23],
      [24, 5],
    ] as const)
      p.px(x, y, R.gold.l2);
  },
  // ตำราดาบคลื่นซัด (epic): navy cover, a dao cutting through a wave crest.
  'dg-manual-wave-cut': (p) => {
    book(p, navy);
    daoBlade(p, 18, 23, 25, 8, 1.6, R.steel, { belly: 0.6, groove: false });
    sprite(p, 15, 20, ['..ww...', '.w..w..', 'w.ww.ww', 'wwwwwww'], { w: R.azure.l1 });
  },
});
